import type { paths } from '$lib/generated/openapi';

export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';
export const API_SERVER_ERROR_EVENT = 'api:server-error';

const SAFE_SERVER_ERROR_MESSAGES = new Set([
  'Erreur lors de la génération audio',
  'La génération audio a dépassé le délai maximal autorisé'
]);

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const DEFAULT_TIMEOUT_MS = 15_000;

export interface ApiFetchInit extends RequestInit {
  timeoutMs?: number;
}

export type ApiPath = keyof paths & string;
type ApiMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';
export type OperationAt<Path extends ApiPath, Method extends ApiMethod> = Method extends keyof paths[Path]
  ? NonNullable<paths[Path][Method]>
  : never;
export type MethodsForPath<Path extends ApiPath> = {
  [Method in ApiMethod]: OperationAt<Path, Method> extends never ? never : Method;
}[ApiMethod];
type JsonRequestBody<Operation> = Operation extends {
  requestBody: { content: { 'application/json': infer Body } };
} ? Body : never;
type PathParameters<Operation> = Operation extends {
  parameters: { path: infer Parameters };
} ? Parameters : never;
type QueryParameters<Operation> = Operation extends {
  parameters: { query?: infer Parameters };
} ? Parameters : never;
type JsonResponse<Operation> = Operation extends { responses: infer Responses }
  ? {
      [Status in keyof Responses]: Status extends number
        ? `${Status}` extends `2${string}`
          ? Responses[Status] extends { content: { 'application/json': infer Body } }
            ? Body
            : never
          : never
        : never;
    }[keyof Responses]
  : never;

export type ApiOperationJsonResponse<
  Path extends ApiPath,
  Method extends MethodsForPath<Path>
> = JsonResponse<OperationAt<Path, Method>>;

type BodyOption<Operation> = [JsonRequestBody<Operation>] extends [never]
  ? { body?: never }
  : { body: JsonRequestBody<Operation> };
type PathParameterOption<Operation> = [PathParameters<Operation>] extends [never]
  ? { pathParams?: never }
  : { pathParams: PathParameters<Operation> };
type QueryParameterOption<Operation> = [QueryParameters<Operation>] extends [never]
  ? { queryParams?: never }
  : { queryParams?: QueryParameters<Operation> };
export type ApiOperationOptions<Operation> = Omit<ApiFetchInit, 'body' | 'method'> &
  BodyOption<Operation> & PathParameterOption<Operation> & QueryParameterOption<Operation>;

export type ApiOperationResponse<Body> = Omit<Response, 'json'> & {
  json(): Promise<Body>;
};

export async function apiOperation<
  Path extends ApiPath,
  Method extends MethodsForPath<Path>
>(
  path: Path,
  method: Method,
  options: ApiOperationOptions<OperationAt<Path, Method>>
): Promise<ApiOperationResponse<JsonResponse<OperationAt<Path, Method>>>> {
  const { body, pathParams, queryParams, ...init } = options;
  let resolvedPath = path as string;

  for (const [name, value] of Object.entries(pathParams ?? {})) {
    resolvedPath = resolvedPath.replace(`{${name}}`, encodeURIComponent(String(value)));
  }
  if (/\{[^}]+\}/.test(resolvedPath)) {
    throw new Error(`Paramètre de chemin manquant pour ${path}`);
  }
  if (queryParams) {
    const params = new URLSearchParams();
    for (const [name, value] of Object.entries(queryParams)) {
      if (value !== undefined && value !== null) params.set(name, String(value));
    }
    const query = params.toString();
    if (query) resolvedPath += `${resolvedPath.includes('?') ? '&' : '?'}${query}`;
  }

  const headers = new Headers(init.headers);
  if (body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await apiFetch(resolvedPath, {
    ...init,
    method: method.toUpperCase(),
    headers,
    ...(body === undefined ? {} : { body: JSON.stringify(body) })
  });
  return response as ApiOperationResponse<JsonResponse<OperationAt<Path, Method>>>;
}

export class ApiFetchError extends Error {
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | null;

  constructor(message: string, status: number, details?: unknown, requestId: string | null = null) {
    super(message);
    this.name = 'ApiFetchError';
    this.status = status;
    this.details = details;
    this.requestId = requestId;
  }
}

export function getApiErrorSupportReference(error: unknown): string | null {
  if (!(error instanceof ApiFetchError) || error.status < 500 || error.status > 599) return null;
  const requestId = error.requestId;
  return requestId && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/.test(requestId) ? requestId : null;
}

export function getSafeServerErrorMessage(message: unknown): string | null {
  if (typeof message !== 'string') return null;
  const normalizedMessage = message.trim();
  return SAFE_SERVER_ERROR_MESSAGES.has(normalizedMessage) ? normalizedMessage : null;
}

function notifyServerError(error: ApiFetchError): void {
  const requestId = getApiErrorSupportReference(error);
  if (requestId && typeof window !== 'undefined') {
    const userMessage = getSafeServerErrorMessage(error.message);
    window.dispatchEvent(new CustomEvent(API_SERVER_ERROR_EVENT, {
      detail: { requestId, ...(userMessage ? { userMessage } : {}) }
    }));
  }
}

function createRequestId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  const values = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(values);
  } else {
    values.forEach((_, index) => { values[index] = Math.floor(Math.random() * 256); });
  }
  return Array.from(values, value => value.toString(16).padStart(2, '0')).join('');
}

function resolveUrl(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof input !== 'string' || !input.startsWith('/')) return input;
  return `${API_URL}${input}`;
}

function shouldInvalidateUserSession(input: RequestInfo | URL): boolean {
  const requestUrl = input instanceof URL
    ? input.href
    : typeof input === 'string'
      ? input
      : input.url;
  const pathname = new URL(requestUrl, API_URL).pathname.replace(/\/+$/, '') || '/';

  return pathname !== '/auth/login'
    && pathname !== '/auth/verify'
    && pathname !== '/admin'
    && !pathname.startsWith('/admin/')
    && pathname !== '/api/admin'
    && !pathname.startsWith('/api/admin/');
}

export function apiUrl(path: string): string {
  return `${API_URL}${path}`;
}

async function parseError(response: Response): Promise<{ message: string; details?: unknown }> {
  if (response.status === 401) {
    return { message: 'Votre session a expiré. Merci de vous reconnecter.' };
  }
  const responseBody = typeof response.clone === 'function' ? response.clone() : response;
  const body = typeof responseBody.text === 'function'
    ? await responseBody.text().catch(() => '')
    : '';
  if (!body && typeof response.json === 'function') {
    const details: unknown = await response.json().catch(() => undefined);
    const message = errorMessage(details);
    if (message) return { message, details };
  }
  if (!body) return { message: `Erreur HTTP ${response.status}` };

  try {
    const details: unknown = JSON.parse(body);
    const message = errorMessage(details);
    if (message) return { message, details };
    return { message: `Erreur HTTP ${response.status}`, details };
  } catch {
    return { message: `Erreur ${response.status}: ${body.slice(0, 500)}`, details: body };
  }
}

function errorMessage(value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const payload = value as { detail?: unknown; message?: unknown; error?: unknown };
  const error = typeof payload.error === 'object' && payload.error !== null
    ? (payload.error as { message?: unknown })
    : undefined;
  const message = error?.message ?? payload.detail ?? payload.message;
  return typeof message === 'string' && message.trim() ? message : undefined;
}

export async function apiFetch(input: RequestInfo | URL, init: ApiFetchInit = {}): Promise<Response> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, signal, ...requestInit } = init;
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort(signal?.reason);
  signal?.addEventListener('abort', abortFromCaller, { once: true });
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const inputHeaders = typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined;
    const headers = new Headers(requestInit.headers ?? inputHeaders);
    headers.set('X-Request-ID', createRequestId());
    const response = await fetch(resolveUrl(input), {
      ...requestInit,
      headers,
      credentials: requestInit.credentials ?? 'include',
      signal: controller.signal
    });

    if (response.status === 401 && typeof window !== 'undefined' && shouldInvalidateUserSession(input)) {
      window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
    }
    if (!response.ok) {
      const error = await parseError(response);
      const apiError = new ApiFetchError(error.message, response.status, error.details, response.headers?.get('X-Request-ID') ?? null);
      notifyServerError(apiError);
      throw apiError;
    }
    return response;
  } catch (error) {
    if (error instanceof ApiFetchError) throw error;
    if (controller.signal.aborted) {
      throw new Error(signal?.aborted ? 'Requête annulée' : 'La requête a expiré');
    }
    const reason = error instanceof Error && error.message ? `: ${error.message}` : '';
    throw new Error(`Erreur de connexion au serveur${reason}`);
  } finally {
    globalThis.clearTimeout(timeout);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}
